/* eslint-disable sort-keys */
/* eslint-disable no-useless-escape */
import * as ents from '@attain-apps/entities'
import { capitalize } from '@attain-apps/string-utils'

// TODO: Find better way to get all property keys besides "subqueries" from DmsQuery since this way of hardcoding values is prone to errors if any of said properties change its name.
/**
 * During the extraction of the current node path @see {@link CompletionProvider._getNodePath}, @see {@link CompletionProvider_removeDmsqlObjectsAndKeywords}
 * there are some rare cases when extracted {@link NodePath} has some of the leftover keywords from the query. When leftover keywords are removed, path
 * to the node is correct. This array represents those keywords.
 */
const DMS_QL_PROBLEMATIC_KEYWORDS = [
  'ref',
  'filter',
  'target',
  'selection',
  'attr',
  'inherit',
  'using',
  'variables',
  'content',
]

// NOTE: Exclude the /g from these regexes as they won't be idempotent if the /g flag is present: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/lastIndex
const COMPLETION_TYPE_REGEX_REF_ATTR = /\s*"attr"\s*:\s*/m
const COMPLETION_TYPE_REGEX_TARGET = /\s*"target"\s*:\s*/m
const COMPLETION_TYPE_REGEX_GET_ATTR = /\s*"@."\s*:\s*/m

/**
 * Node path is represented as an readonly array of subquery names without root included
 * Example:
 * /sub2/sub3 -> ['sub2', 'sub3']
 */
type NodePath = readonly string[]

/**
 * Node may have properties of a {@link ents.core.dms.data.query.Query} or an {@link ents.core.dms.data.query.Subquery}. It's extracted from the JSON string which can be incomplete, thus we have to manually check if properties
 * exist or not when working with this class.
 */
type Node = ents.core.dms.data.query.Query & ents.core.dms.data.query.Subquery

/**
 * Wrapper class for DMS Query Language completion
 *
 * To extract needed information from the outputs provided from the Monaco editor (cursor position, text model) in this class we observe provided query
 * as a tree structure.
 *
 * Example:
 *
 * ```json
 * {
 *  "target": "!ent:!locations:!country",
 *    "subqueries": {
 *      "sub1": {
 *        "target": "!ent:!locations:!address"
 *      },
 *      "sub2": {
 *        "target": "!ent:!locations:!continent",
 *        "subqueries": {
 *          "sub3": {
 *            "target": "!ent:!locations:!city"
 *          }
 *        }
 *      }
 *    }
 * }
 *```
 * This query would be represented as:
 * ```
 * /
 * ├── sub1
 * └── sub2
 *      └── sub3
 *```
 * Most of the custom suggestions we want to provide are influenced by parent or sibling subqueries. To find information from related subqueries we first find
 * the {@link NodePath} of nodes which leads to cursor position from where the suggestion is requested. We do that by taking the top half of the query -> from the begining
 * text model to the current cursor position, and processing it with regex {@link _getNodePath}.
 *
 * From text model we can parse the whole query as an object and then access relevant nodes which have an impact on the suggestion. From that point onward, we
 * build specific logic for each suggestion type.
 *
 */
export class CompletionProvider {
  /** @see {@link ents.core.dms.models.Schema} */
  private readonly _schema: ents.core.dms.models.Schema

  constructor(schema: ents.core.dms.models.Schema) {
    this._schema = schema
  }

  /**
   * Returns suggestions when writing DMSQL.
   *
   * Implemented:
   * - Ref Attribute
   *
   * TODO:
   * - Target (has implemented logic for finding relevant targets, only shcema logic is required)
   * - Get Attribute
   * - Get Subquery
   * - Variables (medium-hard; requires additional context from the page)
   * - Ref Inherit
   * - Using
   * - And more...
   *
   */
  public getSuggestions(
    model: monaco.editor.ITextModel,
    position: monaco.Position,
  ): monaco.languages.CompletionList {
    if (!this._schema.areModelsInitialized) {
      console.warn(
        'Schema models are not initialized. Check DMS page to see if models are loaded',
      )
      return { suggestions: [] }
    }

    const { query, queryUntilCursorPosition, type, range } =
      this._getSuggestionParams(model, position)
    const editorContext: CompletionProvider.EditorContext = {
      model,
      position,
      range,
    }

    try {
      const targetedNodePath = this._getNodePath(queryUntilCursorPosition)
      const queryObject = JSON.parse(this._normalizeJsonString(query)) as Node

      switch (type) {
        case CompletionProvider.CompletionType.Target:
          return {
            suggestions: this._getTargetSuggestions(
              queryObject,
              targetedNodePath,
              editorContext,
            ),
          }

        case CompletionProvider.CompletionType.RefAttribute:
          return {
            suggestions: this._getRefAttributeSuggestions(
              queryObject,
              targetedNodePath,
              editorContext,
            ),
          }

        case CompletionProvider.CompletionType.GetAttribute:
          return {
            suggestions: this._getModelAttributeSuggestions(
              queryObject,
              targetedNodePath,
              editorContext,
            ),
          }

        case undefined:
        default:
          return { suggestions: [] }
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error(`Completion provider failed with error: ${error.message}`)
      }
      return { suggestions: [] }
    }
  }

  /**
   * Find all posible targets that are related to relevant targets from the query.
   * See this discussion about the logic: https://github.com/attain-apps/attain-apps/pull/246#discussion_r2212167893
   */
  private _getTargetSuggestions(
    root: Node,
    selectedNodePath: NodePath,
    editorContext: CompletionProvider.EditorContext,
  ) {
    const allModels = [
      ...this._schema.dimensions,
      ...this._schema.entities,
      ...this._schema.snapshots,
    ]
    // If target is on the root query, we suggest all posible targets except the relations
    if (selectedNodePath.length === 0) {
      return this._formatAsCompletionItemList({
        suggestions: allModels,
        editorContext,
      })
    }

    // Parent node is important for relation targets suggestions
    const parentNodePath = this._getParentNodePath(selectedNodePath)
    const parentNode = this._getNodeByPath(root, parentNodePath)
    const parentNodeTarget = this._getModelIdFromTarget(
      root,
      parentNodePath,
      parentNode.target,
    )

    const modelIdsFromRelevantTargets = this._getModelIdsFromRelevantNodes(
      root,
      selectedNodePath,
    )

    const suggestedModels: (
      | ents.core.dms.models.Dimension
      | ents.core.dms.models.ForRecord
    )[] = []
    const suggestedRelations: {
      id: ents.core.dms.data.query.Relation
      name: string
    }[] = []

    for (const modelId of modelIdsFromRelevantTargets) {
      if (ents.core.dms.models.Dimension.Id.isId(modelId)) {
        // If the target is a dimension:
        suggestedModels.push(
          // 1. For any attribute that references this dimension -> the dimension, entity, or snapshot that contains that attribute
          ...allModels.filter(model =>
            model.attributes?.some(
              attribute =>
                ents.core.dms.data.Value.Type.Reference.isReference(
                  attribute.type,
                ) && attribute.type.dimensionId === modelId,
            ),
          ),

          // 2. For any attribute of this dimension that references other entity's dimension -> the dimension being referenced and it's entities
          ...(this._schema.getDimension(modelId).attributes ?? []).flatMap(
            attribute =>
              ents.core.dms.data.Value.Type.Reference.isReference(
                attribute.type,
              )
                ? [
                    this._schema.getDimension(attribute.type.dimensionId),
                    ...this._schema.getEntitiesInDimension(
                      attribute.type.dimensionId,
                    ),
                  ]
                : [],
          ),

          // 3. Any snapshots available to the dimension
          ...this._schema.getSnapshotsForDimension(modelId),
        )
      } else if (ents.core.dms.models.Entity.Id.isId(modelId)) {
        // If the target is a entity:
        const dim = this._schema.getDimension(
          ents.core.dms.models.Dimension.Id.from(modelId),
        )
        suggestedModels.push(
          // 1. All other entities in the entity's dimension
          ...(dim.entities ?? []).filter(e => e.id !== modelId),

          // 2. For any attribute that references this entity's dimension -> the dimension, entity, or snapshot that contains that attribute
          ...allModels.filter(model =>
            model.attributes?.some(
              attribute =>
                ents.core.dms.data.Value.Type.Reference.isReference(
                  attribute.type,
                ) && attribute.type.dimensionId === dim.id,
            ),
          ),

          // 3. For any attribute of this entity that references other entity's dimension -> the dimension being referenced and it's entities
          ...(this._schema.getEntity(modelId).attributes ?? []).flatMap(
            attribute =>
              ents.core.dms.data.Value.Type.Reference.isReference(
                attribute.type,
              )
                ? [
                    this._schema.getDimension(attribute.type.dimensionId),
                    ...this._schema.getEntitiesInDimension(
                      attribute.type.dimensionId,
                    ),
                  ]
                : [],
          ),

          // 4. Any snapshots associated with the entity's dimension
          ...this._schema.getSnapshotsForDimension(dim.id),
        )
      } else if (ents.core.dms.models.Snapshot.Id.isId(modelId)) {
        // If the target is a snapshot:
        const dimId = ents.core.dms.models.Dimension.Id.from(modelId)
        if (dimId) {
          // 1. The dimension and all entities in the dimension that the snapshot is associated with
          const dim = this._schema.getDimension(dimId)
          suggestedModels.push(dim, ...(dim.entities ?? []))
        }
      }
    }

    if (ents.core.dms.models.Dimension.Id.isId(parentNodeTarget)) {
      // If parent node target is a dimension, all relation targets are valid suggestions
      suggestedRelations.push(
        ...ents.core.dms.data.query.Relation.ALL.map(relation => ({
          id: relation,
          name: capitalize(String(relation)),
        })),
      )
    } else if (ents.core.dms.models.Entity.Id.isId(parentNodeTarget)) {
      // If parent node target is entity we have to check it's position in the dimension to determine possible relation targets
      const entity = this._schema.getEntity(parentNodeTarget)
      // If the entity is the root entity, there is no parent, ancestors and root relations
      if (this._schema.isRoot(entity)) {
        suggestedRelations.push(
          { id: 'child', name: 'Child' },
          { id: 'leaf', name: 'Leaf' },
          { id: 'descendants', name: 'Descendants' },
        )
      } else {
        // If the entity is the leaf entity, there is no child, descendants and leaf relations, if not all relations are valid suggestions
        if (this._schema.isLeaf(entity)) {
          suggestedRelations.push(
            { id: 'parent', name: 'Parent' },
            { id: 'root', name: 'Root' },
            { id: 'ancestors', name: 'Ancestors' },
          )
        } else {
          suggestedRelations.push(
            ...ents.core.dms.data.query.Relation.ALL.map(relation => ({
              id: relation,
              name: capitalize(String(relation)),
            })),
          )
        }
      }
    }

    const allSuggestions = [
      ...suggestedModels.reduce(
        (newArray, schemaModel) => {
          if (!newArray.some(el => el.id === schemaModel.id)) {
            newArray.push(schemaModel)
          }
          return newArray
          // Here we take first element of the suggestedModels as the initial value of the reduce method as an easy way to define the type of the array
        },
        [suggestedModels[0]],
      ),
      ...suggestedRelations.map(relation => ({
        // There is a need to cast every relation id as a Relation because when adding suggested relations they are defined as strings
        id: relation.id,
        name: relation.name,
      })),
    ]
    return this._formatAsCompletionItemList({
      suggestions: allSuggestions,
      editorContext,
    })
  }

  /**
   * Generate suggestions for the {@link ents.core.dms.data.query.Ref.attr } of a {@link ents.core.dms.data.query.Subquery}
   */
  private _getRefAttributeSuggestions(
    root: Node,
    selectedNodePath: NodePath,
    editorContext: CompletionProvider.EditorContext,
  ): monaco.languages.CompletionItem[] {
    const selectedNode = this._getNodeByPath(root, selectedNodePath)

    // We can not suggest a ref attribute without knowing what is the target of the subquery
    if (selectedNode.target === undefined) {
      return []
    }
    const connectedByNodePath = selectedNode.using
      ? ([
          ...this._getParentNodePath(selectedNodePath),
          selectedNode.using,
        ] as NodePath)
      : this._getParentNodePath(selectedNodePath)
    const connectedByNode = this._getNodeByPath(root, connectedByNodePath)

    const selectedNodeTarget = this._getModelIdFromTarget(
      root,
      selectedNodePath,
      selectedNode.target,
    )
    const connectedByNodeTarget = this._getModelIdFromTarget(
      root,
      connectedByNodePath,
      connectedByNode.target,
    )

    if (
      selectedNodeTarget === undefined ||
      connectedByNodeTarget === undefined
    ) {
      return []
    } else {
      const connectingRefAttributes =
        this._schema.getConnectingReferenceAttributes(
          selectedNodeTarget,
          connectedByNodeTarget,
        )

      return this._formatAsCompletionItemList({
        suggestions: connectingRefAttributes.reduce(
          (newArray, attribute) => {
            if (!newArray.some(el => el.id === attribute.id)) {
              newArray.push(attribute)
            }
            return newArray
            // Here we take first element of the suggestedModels as the initial value of the reduce method as an easy way to define the type of the array
          },
          [connectingRefAttributes[0]],
        ),
        editorContext,
      })
    }
  }

  /**
   * Generates suggestions for the {@link ents.core.dms.data.query.Query.selection} -> {@link ents.core.dms.data.expression.fn.definitions.GetAttribute}
   */
  private _getModelAttributeSuggestions(
    root: Node,
    selectedNodePath: NodePath,
    editorContext: CompletionProvider.EditorContext,
  ) {
    const selectedNode = this._getNodeByPath(root, selectedNodePath)

    // We can not suggest selection attributes without knowing what is the target of the query
    const selectedNodeModelId = this._getModelIdFromTarget(
      root,
      selectedNodePath,
      selectedNode.target,
    )
    if (!selectedNodeModelId) {
      return []
    }

    return this._formatAsCompletionItemList({
      suggestions: this._schema.getAttributesOf(selectedNodeModelId),
      editorContext,
      valueTransformation: (model: ents.core.dms.models.Attribute) =>
        ents.core.dms.models.Id.Parts.from(model).partialId,
      labelTransformation: (model: ents.core.dms.models.Attribute) =>
        `${model.name} (${ents.core.dms.models.Id.Parts.from(model).partialId})`,
    })
  }

  /**
   * Targets that are relevant to node which asks for a target suggestion are targets of it's parent node and it's sibling nodes.
   * Easiest way to do this is finding parent node and its target from {@param relevantNodePath} and then get targets
   * of all its subqueries(children nodes) excluding the node we are finding suggestion for.
   */
  private _getModelIdsFromRelevantNodes(
    root: Node,
    relevantNodePath: NodePath,
  ) {
    const modelIdTargets: (
      | ents.core.dms.models.Dimension.Id
      | ents.core.dms.models.ForRecord.Id
    )[] = []

    if (relevantNodePath.length === 0) {
      return modelIdTargets
    }

    const parentNodePath = this._getParentNodePath(relevantNodePath)
    const parentNode = this._getNodeByPath(root, parentNodePath)

    const modelId = this._getModelIdFromTarget(
      root,
      parentNodePath,
      parentNode.target,
    )
    if (modelId) {
      modelIdTargets.push(modelId)
    }

    // After finding the parent node of the node we want suggestions on we go through all
    // of its subqueries (except the node we want suggestions on) and try to resolve their
    // targets into a modelId. If successful we return those targets.
    for (const nodeName of Object.keys(parentNode.subqueries ?? {})) {
      if (
        nodeName !== relevantNodePath.at(-1) &&
        parentNode.subqueries !== undefined
      ) {
        const subqueryModelId = this._getModelIdFromTarget(
          root,
          [...parentNodePath, nodeName] as NodePath,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          parentNode.subqueries[nodeName]!.target,
        )

        if (subqueryModelId) {
          modelIdTargets.push(subqueryModelId)
        }
      }
    }
    return modelIdTargets
  }

  /**
   * Resolves {@link ents.core.dms.data.query.Relation} based target into a {@link ents.core.dms.models.Dimension.Id} or a {@link ents.core.dms.models.ForRecord.Id}.
   */
  private _resolveRelationTarget(
    params: CompletionProvider.ResolveRelationTargetMethodParams,
  ) {
    const { parentNodeTarget, relationTarget } = params
    if (
      ents.core.dms.data.query.Target.DateRange.List.isList(parentNodeTarget)
    ) {
      throw new Error(
        `Could not determine relation target out of date range parent target`,
      )
    }

    if (
      ents.core.dms.data.query.Relation.Where.Singleton.isSingleton(
        relationTarget,
      ) &&
      ents.core.dms.models.Entity.Id.isId(parentNodeTarget)
    ) {
      return this._schema.getRelative(parentNodeTarget, relationTarget).id
    } else {
      // If targeting ancestors/descendants we are basically targeting the dimension of the parent target
      const dimId =
        ents.core.dms.models.Dimension.Id.isId(parentNodeTarget) ||
        ents.core.dms.models.ForRecord.Id.isId(parentNodeTarget)
          ? ents.core.dms.models.Dimension.Id.from(parentNodeTarget)
          : undefined
      if (!dimId) {
        throw new Error(
          `Could not extract dimension id out of target: ${parentNodeTarget.toString()}`,
        )
      }

      return dimId
    }
  }

  /**
   * Tries to adapt some missing features of the JSON string to make it parsable into an object
   */
  private _normalizeJsonString(input: string) {
    return input
      .replaceAll(/\s+/g, '')
      .replaceAll(':,', ':"",')
      .replaceAll(':}', ':""}')
  }

  /**
   * Tries to remove all object and keywords from the query which do not represent Subquery names.
   * This is requred in order to extract @see {@link NodePath}.
   */
  // eslint-disable-next-line @typescript-eslint/member-ordering
  private static _removeDmsqlObjectsAndKeywords(query: string) {
    let lastQuery = query
    let currentQuery = query.replaceAll(
      /\"[\w\@\=\.\#\$\~\!\%\^\&\*\+\-\>\<\?\_\`\/\\\{\}\[\]]+\"\:((\{[^{^}]*\})|(\[[^\[^\]]*\])|(\"[\w\@\=\.\#\$\~\!\%\^\&\*\+\-\>\<\?\_\`\/\\\{\}\[\]]*\")|(newDate\('[\d\-]+'\))|(true|false)|newRange\([\{\},]*\))|(\[[\{\}\,\d\"\"\:\w]*\])|([\[\(][\d\-,]*[\]\)])/gm,
      '',
    )
    while (currentQuery !== lastQuery) {
      lastQuery = currentQuery
      currentQuery = currentQuery.replaceAll(
        /\"[\w\@\=\.\#\$\~\!\%\^\&\*\+\-\>\<\?\_\`\/\\\{\}\[\]]+\"\:((\{[^{^}]*\})|(\[[^\[^\]]*\])|(\"[\w\@\=\.\#\$\~\!\%\^\&\*\+\-\>\<\?\_\`\/\\\{\}\[\]]*\")|(newDate\('[\d\-]+'\))|(true|false)|newRange\([\{\},]*\))|(\[[\{\}\,\d\"\"\:\w]*\])|([\[\(][\d\-,]*[\]\)])/gm,
        '',
      )
    }

    // eslint-enable no-useless-escape
    DMS_QL_PROBLEMATIC_KEYWORDS.forEach(
      kw => (currentQuery = currentQuery.replaceAll(kw, '')),
    )

    return currentQuery
  }

  /**
   * Extracts {@link NodePath} from the query written up to the position of the cursor
   */
  private _getNodePath(halfQuery: string): NodePath {
    let query = this._normalizeJsonString(halfQuery)
    query = CompletionProvider._removeDmsqlObjectsAndKeywords(query)

    const nodeNamesMatched = query.match(/"(?!(?:subqueries)")(\w+)"\:\{/gm)
    return nodeNamesMatched
      ? nodeNamesMatched.map(i => i.replaceAll('"', '').replaceAll(':{', ''))
      : []
  }

  /**
   *
   */
  private _getParentNodePath(path: NodePath) {
    return path.slice(0, -1) as NodePath
  }

  /**
   * Returnes {@link Node} object based on the {@link NodePath}
   */
  private _getNodeByPath(root: Node, path: NodePath) {
    if (path.length === 0) {
      return root
    }

    let node = root
    for (const nodeName of path) {
      if (node.subqueries === undefined) {
        throw new Error('Invalid path for provided query')
      }
      node = node.subqueries[nodeName] as Node
    }

    return node
  }

  /**
   * Extracts {@link CompletionProvider.Params} from the current editor state
   */
  private _getSuggestionParams(
    model: monaco.editor.ITextModel,
    position: monaco.Position,
  ): CompletionProvider.Params {
    const query = model.getValue()
    /* eslint-disable sort-keys */
    const queryUntilCursorPosition = model.getValueInRange({
      startLineNumber: 1,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: position.column,
    })
    const word = model.getWordUntilPosition(position)
    const range = {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: word.startColumn,
      endColumn: word.endColumn,
    }
    const type = this._determineCompletionType(model, position)

    return {
      query,
      queryUntilCursorPosition,
      range,
      type,
    }
  }

  /**
   * Tries to determine completion type based on the surrounding keywords of the current cursor position in the editor
   */
  private _determineCompletionType(
    model: monaco.editor.ITextModel,
    position: monaco.Position,
  ): CompletionProvider.CompletionType | undefined {
    /* eslint-disable sort-keys */
    const lineContent = model.getValueInRange({
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: 1,
      endColumn: position.column,
    })
    /* eslint-enable sort-keys */

    if (COMPLETION_TYPE_REGEX_REF_ATTR.test(lineContent)) {
      return CompletionProvider.CompletionType.RefAttribute
    } else if (COMPLETION_TYPE_REGEX_TARGET.test(lineContent)) {
      return CompletionProvider.CompletionType.Target
    } else if (COMPLETION_TYPE_REGEX_GET_ATTR.test(lineContent)) {
      return CompletionProvider.CompletionType.GetAttribute
    }

    return undefined
  }

  /**
   * Returns the provided value wrapped in the ``""`` if there is no ``""`` on the current cursor position
   */
  private _ident(
    value: string,
    model: monaco.editor.ITextModel,
    position: monaco.IPosition,
  ): string {
    /* eslint-disable sort-keys */
    const adjacentText = model.getValueInRange({
      startColumn: position.column - 1,
      endColumn: position.column + 1,
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
    })
    /* eslint-enable sort-keys */

    return adjacentText.includes('""') ? value : `"${value}"`
  }

  /**
   * Tries to resolve target from specified query/subquery into the Dimension or
   * models.ForRecord Id. If impossible returns `undefined`.
   */
  private _getModelIdFromTarget(
    root: Node,
    path: NodePath,
    target: ents.core.dms.data.query.Target,
  ):
    | ents.core.dms.models.Dimension.Id
    | ents.core.dms.models.ForRecord.Id
    | undefined {
    if (!ents.core.dms.data.query.Target.DateRange.List.isList(target)) {
      if (ents.core.dms.data.query.Relation.isRelation(target)) {
        return this._resolveRelationTarget({
          parentNodeTarget: this._getNodeByPath(
            root,
            this._getParentNodePath(path),
          ).target,
          relationTarget: target,
        })
      } else {
        return target
      }
    } else {
      return undefined
    }
  }

  /**
   * Format provided suggestions into a {@link monaco.languages.CompletionItem} array.
   */
  private _formatAsCompletionItemList(
    params: CompletionProvider.FormatAsCompletionItemListParams,
  ) {
    const {
      suggestions,
      editorContext,
      valueTransformation,
      labelTransformation,
    } = params
    const { model, position, range } = editorContext
    return suggestions.map<monaco.languages.CompletionItem>(schemaModel => ({
      insertText: this._ident(
        valueTransformation ? valueTransformation(schemaModel) : schemaModel.id,
        model,
        position,
      ),
      insertTextRules:
        monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
      kind: monaco.languages.CompletionItemKind.Field,
      label: labelTransformation
        ? labelTransformation(schemaModel)
        : `${schemaModel.name} (${schemaModel.id})`,
      range: range,
    }))
  }
}
export namespace CompletionProvider {
  export enum CompletionType {
    Target = 'target',
    RefAttribute = 'refAttribute',
    GetAttribute = 'getAttribute',
  }

  export interface Params {
    /**
     * Full query from the editor component represented as string.
     */
    query: string
    /**
     * Part of the query from the starting editor position (line 1, column 1) to the current position of the cursor.
     */
    queryUntilCursorPosition: string
    /**
     * Type of completion requested. Has to be determined using regex.
     */
    type?: CompletionType

    /**
     * A range of text that should be replaced by a completion item. Parameter required by {@link monaco.languages.CompletionItem}.
     */
    range: monaco.IRange
  }

  export interface EditorContext {
    model: monaco.editor.ITextModel
    position: monaco.IPosition
    range: monaco.IRange
  }

  export interface ResolveRelationTargetMethodParams {
    /**
     * Target of the parent node on which the relation target is based
     */
    parentNodeTarget: ents.core.dms.data.query.Target

    /**
     * Relation target to be resolved into an {@link ents.core.dms.models.Dimension.Id} or {@link ents.core.dms.models.ForRecord.Id}
     */
    relationTarget: ents.core.dms.data.query.Relation
  }

  export interface FormatAsCompletionItemListParams {
    suggestions: (
      | ents.core.dms.models.Attribute
      | ents.core.dms.models.Dimension
      | ents.core.dms.models.ForRecord
      | { id: ents.core.dms.data.query.Relation; name: string }
    )[]
    editorContext: CompletionProvider.EditorContext
    valueTransformation?: (model: any) => string
    labelTransformation?: (model: any) => string
  }
}
